// localStorage + server persistence for AI-generated variants (M4).
//
// Generated variants live only in memory during a session (App.attachGeneratedVariant
// pushes them into the live RoomsConfig). This module saves them to localStorage
// under 'imersivo:generated-variants' so they survive page refreshes, and
// optionally syncs to the server (Vercel Blob) so they persist across devices.
//
// Cross-device sync: each browser gets a random UUID "project ID" stored in
// localStorage. POST /api/variants/save uploads the full array; GET
// /api/variants/load?projectId=<uuid> fetches it back. The UI lets the user
// copy their project ID and paste it on another device to import variants.
//
// Max 20 entries per room (oldest pruned first) to avoid unbounded growth.

import type { Room, Variant } from './types';

const STORAGE_KEY = 'imersivo:generated-variants';
const PROJECT_ID_KEY = 'imersivo:project-id';
const MAX_PER_ROOM = 20;

export interface PersistedVariant {
  roomId: string;
  variant: Variant;
  /** ISO timestamp — used for LRU pruning. */
  savedAt: string;
}

/**
 * Merge persisted generated variants into live rooms, in place. Skips unknown
 * rooms and variants already present (by id). Returns the count newly added.
 *
 * Pure (no DOM / network / storage) so it's unit-testable and shared by both
 * rehydrate() (localStorage path) and importFromServer() (cross-device path).
 */
export function mergeGeneratedVariants(rooms: Room[], incoming: PersistedVariant[]): number {
  const roomById = new Map(rooms.map((r) => [r.id, r]));
  let count = 0;
  for (const entry of incoming) {
    if (!entry || typeof entry.roomId !== 'string' || !entry.variant?.id) continue;
    const room = roomById.get(entry.roomId);
    if (!room) continue;
    if (room.variants.some((v) => v.id === entry.variant.id)) continue;
    room.variants.push(entry.variant);
    count++;
  }
  return count;
}

function load(): PersistedVariant[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as PersistedVariant[];
  } catch {
    return [];
  }
}

function save(entries: PersistedVariant[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage quota exceeded — prune oldest half and retry once.
    const half = entries.slice(Math.floor(entries.length / 2));
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(half));
    } catch {
      // Give up silently — generated variants are a convenience, not critical.
    }
  }
}

// ── Project ID ─────────────────────────────────────────────────────

/** Returns (or creates) the persistent UUID identifying this browser's project. */
export function getProjectId(): string {
  try {
    const existing = localStorage.getItem(PROJECT_ID_KEY);
    if (existing && /^[0-9a-f-]{36}$/.test(existing)) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(PROJECT_ID_KEY, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

/** Replace this browser's project ID (e.g. after importing from another device). */
export function setProjectId(id: string): void {
  try {
    localStorage.setItem(PROJECT_ID_KEY, id);
  } catch {
    // ignore
  }
}

// ── Server sync ────────────────────────────────────────────────────

/** Upload all local variants to the server. Fire-and-forget; errors are logged but not thrown. */
export function syncToServer(): void {
  const entries = load();
  if (entries.length === 0) return;
  const projectId = getProjectId();
  fetch('/api/variants/save', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ projectId, variants: entries }),
  }).catch((err: unknown) => {
    console.warn('[variants] server sync failed', err);
  });
}

/**
 * Download variants saved under `projectId` from the server and merge them
 * into the provided rooms. Variants already present (by id) are skipped.
 * Returns the number of newly imported variants.
 */
export async function importFromServer(projectId: string, rooms: Room[]): Promise<number> {
  const res = await fetch(`/api/variants/load?projectId=${encodeURIComponent(projectId)}`);
  if (!res.ok) {
    if (res.status === 404) return 0;
    throw new Error(`server returned ${res.status}`);
  }
  const body = (await res.json()) as { variants: PersistedVariant[] };
  const incoming = Array.isArray(body.variants) ? body.variants : [];
  if (incoming.length === 0) return 0;

  // Merge into live rooms.
  const count = mergeGeneratedVariants(rooms, incoming);

  // Also persist locally so next refresh doesn't need a server round-trip.
  const local = load();
  const localIds = new Set(local.map((e) => `${e.roomId}:${e.variant.id}`));
  for (const entry of incoming) {
    if (!localIds.has(`${entry.roomId}:${entry.variant.id}`)) {
      local.push(entry);
    }
  }
  save(local);

  return count;
}

// ── Local persistence ──────────────────────────────────────────────

/** Persist a newly generated variant so it survives page refresh. */
export function saveVariant(roomId: string, variant: Variant): void {
  const entries = load();
  // Deduplicate by id in case of double-save.
  const filtered = entries.filter(
    (e) => !(e.roomId === roomId && e.variant.id === variant.id),
  );
  filtered.push({ roomId, variant, savedAt: new Date().toISOString() });

  // Prune oldest entries per room if over the cap.
  const byRoom = new Map<string, PersistedVariant[]>();
  for (const entry of filtered) {
    const group = byRoom.get(entry.roomId) ?? [];
    group.push(entry);
    byRoom.set(entry.roomId, group);
  }
  const pruned: PersistedVariant[] = [];
  for (const group of byRoom.values()) {
    // Sort oldest-first, then keep only the last MAX_PER_ROOM.
    group.sort((a, b) => a.savedAt.localeCompare(b.savedAt));
    pruned.push(...group.slice(-MAX_PER_ROOM));
  }

  save(pruned);
  syncToServer();
}

/**
 * Re-inject persisted generated variants into rooms at startup.
 * Rooms whose id is not present in the config are silently skipped.
 * Variants whose id already exists in the room (e.g. from rooms.json) are skipped.
 */
export function rehydrate(rooms: Room[]): void {
  const entries = load();
  if (entries.length === 0) return;
  mergeGeneratedVariants(rooms, entries);
}

/** All persisted generated variants (newest pruning already applied), for the
 *  "variants you explored" gallery. Read-only snapshot. */
export function listGenerated(): PersistedVariant[] {
  return load();
}

/** Remove all persisted generated variants for a room (e.g. on explicit clear). */
export function clearRoom(roomId: string): void {
  const entries = load().filter((e) => e.roomId !== roomId);
  save(entries);
}

/** Remove all persisted generated variants across all rooms. */
export function clearAll(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
